//region :: Imports ::
import MPage, {MPageProps} from '../Toolkit/Components/Shell/Page';
import MTable, {MTableProps} from '../Toolkit/Components/Controls/Table';
import MComponent from '../Toolkit/Components/Component';
import WorkerService from '../Services/WorkerService';
import {createRef, ReactElement, ReactNode, RefObject} from 'react';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import {Worker} from '../Services/WorkerHubTypes';
import {Column, ColumnFilterElementTemplateOptions} from 'primereact/column';
import {FilterMatchMode} from 'primereact/api';
import {WorkerType} from '../Api/WorkerHubClients';
import {formatDateAsText, getEnumItems, hasRoles, MEnumItem} from '../Toolkit/Utils';
import MMultiDropDown from '../Toolkit/Components/Controls/MultiDropDown';
import {MDropDownItem} from '../Toolkit/Components/Controls/DropDownOption';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import WithRouterProps from '../Toolkit/WithRouterProps';
import MButton from '../Toolkit/Components/Controls/Button';
import {NewIconName, OpenIconName} from '../Toolkit/Icons';
import WithRouter from '../Toolkit/WithRouter';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import {Roles} from "../Roles";
import MAuthProvider from "../Toolkit/Components/Auth/AuthProvider";

//endregion

/** */
type WorkerRow = {
    data: Worker;
}

/** */
type Props = WithMsalProps & WithRouterProps & {}

/** */
type State = {
    page: MPageProps;
    table: MTableProps<WorkerRow>;
    selectedRow?: WorkerRow;
    workerTypeItems: MDropDownItem<WorkerType>[];
}

/** */
class ListWorkersPage extends MComponent<Props, State> {

    private readonly m_workerService: WorkerService;
    private readonly m_pageRef: RefObject<MPage>;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_workerService = new WorkerService(this.props.msalContext);
        this.m_pageRef = createRef<MPage>();

        this.initializeState();

    }

    /** */
    private bindMethods(): void {

        this.isOpenButtonDisabled = this.isOpenButtonDisabled.bind(this);
        this.newButton_onClick = this.newButton_onClick.bind(this);
        this.openButton_onClick = this.openButton_onClick.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.getWorkerTypeFilterTemplate = this.getWorkerTypeFilterTemplate.bind(this);

    }

    /** */
    private initializeState(): void {

        //  Page
        const page: MPageProps = {
            locked: false
        };

        //  Table
        const table: MTableProps<WorkerRow> = {
            rowData: [],
            noRowsMessage: 'No workers found',
            filters: {
                'data.id': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.firstName': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.lastName': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.workerType': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                }
            }
        };

        //  Worker Types
        const workerItemTypes: MDropDownItem<WorkerType>[] = getEnumItems(WorkerType).map(
            (item: MEnumItem) => {
                return {label: item.name, value: item.value};
            });

        this.state = {
            page: page,
            table: table,
            selectedRow: undefined,
            workerTypeItems: workerItemTypes
        };

    }

    /** */
    private isOpenButtonDisabled(): boolean {
        return this.state.selectedRow === undefined;
    }

    /** */
    private newButton_onClick(): void {
        this.props.navigate('/workers/create');
    }

    private openButton_onClick(): void {
        this.props.navigate(`/workers/${this.state.selectedRow!.data.id}`);
    }

    /** */
    private table_onSelectionChange(selection: WorkerRow | WorkerRow[] | undefined): void {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as WorkerRow | undefined);

    }

    /** */
    private async page_onLoadData(): Promise<void> {

        const result = await this.m_workerService.getAll();

        if(result.hasErrors) {
            throw result.exception;
        }

        this.safeSetState((draft: State) => {

            draft.table.rowData = result.reply!.workers!.map((w: Worker) => {
                return {data: w} as WorkerRow;
            });

        });

    }

    /** */
    private getToolbarTemplate(): ReactElement<MToolbar> {
        if (hasRoles([Roles.fullAccess], MAuthProvider.msalContext)) {
            return (

                <MToolbar>

                    <MButton color="success"
                             icon={NewIconName}
                             iconOnly={true}
                             disabled={false}
                             onClick={this.newButton_onClick}/>

                    <MSpacer/>

                    <MButton icon={OpenIconName}
                             iconOnly={true}
                             disabled={this.isOpenButtonDisabled}
                             onClick={this.openButton_onClick}/>
                </MToolbar>

            );
        }
        else {
            return (

                <MToolbar>

                    <MSpacer/>

                    <MButton icon={OpenIconName}
                             iconOnly={true}
                             disabled={this.isOpenButtonDisabled}
                             onClick={this.openButton_onClick}/>
                </MToolbar>

            );
        }

    }

    /** */
    private getWorkerTypeFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {

        return (
            <MMultiDropDown id="workerTypeFilter"
                            items={this.state.workerTypeItems}
                            disabled={false}
                            onChange={(selection: WorkerType[]) => options.filterApplyCallback(selection)}/>
        );

    }

    /** */
    public render(): ReactElement {

        return (
            <MPage title="Workers"
                   locked={this.state.page.locked}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <MTable rowKey="data.id"
                        rowData={this.state.table.rowData}
                        filters={this.state.table.filters}
                        onSelectionChange={this.table_onSelectionChange}>

                    <Column field="data.id"
                            header="Worker ID"
                            filter={true}
                            sortable={true}
                            style={{width: '10%'}}/>

                    <Column field="data.firstName"
                            header="First Name"
                            filter={true}
                            sortable={true}
                            style={{width: '18%'}}/>

                    <Column field="data.lastName"
                            header="Last Name"
                            filter={true}
                            sortable={true}
                            style={{width: '18%'}}/>

                    <Column field="data.workerType"
                            header="Type"
                            filter={true}
                            filterElement={this.getWorkerTypeFilterTemplate}
                            sortable={true}
                            showFilterMenu={false}
                            showClearButton={false}
                            body={(r: WorkerRow) => WorkerType[r.data.workerType!]}
                            style={{width: '15%'}}/>

                    <Column field="data.createdOn"
                            header="Created On"
                            sortable={true}
                            body={(r: WorkerRow) => formatDateAsText(r.data.createdOn!)}
                            style={{width: '15%'}}/>

                    <Column field="data.modifiedOn"
                            header="Modified On"
                            sortable={true}
                            body={(r: WorkerRow) => formatDateAsText(r.data.modifiedOn!)}/>

                </MTable>

            </MPage>
        );

    }

}

export default withMsal(WithRouter(ListWorkersPage));
